import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal } from '../components/Modal.js';
import { Field, StatusBadge } from '../components/Field.js';
import { errorMessage } from '../api/client.js';
import {
  assignSalesRep,
  fetchSalesAccounts,
  type AssignmentType,
  type AssignRepInput,
  type SalesAccount,
} from '../api/endpoints.js';

const ASSIGNMENT_TYPES: AssignmentType[] = [
  'account_owner',
  'ae',
  'sdr',
  'support_rep',
  'reseller_manager',
  'customer_success',
];

export function SalesAccountsPage() {
  const qc = useQueryClient();
  const [assignFor, setAssignFor] = useState<SalesAccount | null>(null);

  const accounts = useQuery({ queryKey: ['salesAccounts'], queryFn: fetchSalesAccounts });

  const columns: Column<SalesAccount>[] = [
    { key: 'name', header: 'Account', render: (a) => a.customerDisplayName },
    { key: 'type', header: 'Type', render: (a) => <span className="badge">{a.orgType}</span> },
    { key: 'status', header: 'Status', render: (a) => <StatusBadge status={a.status} /> },
    {
      key: 'assignments',
      header: 'Assignments',
      render: (a) =>
        a.assignments.length === 0 ? (
          <span className="muted">Unassigned</span>
        ) : (
          a.assignments.map((as) => (
            <span key={as.id} className="badge">
              {as.assignment_type.replace(/_/g, ' ')}: {as.assigned_user_email}
            </span>
          ))
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (a) => (
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAssignFor(a)}>
          Assign rep
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Sales Accounts"
        subtitle="Customer accounts and their commercial assignments (owner, AE, SDR, etc.)."
      />

      <DataTable
        columns={columns}
        rows={accounts.data}
        rowKey={(a) => a.customerOrgId}
        isLoading={accounts.isLoading}
        isError={accounts.isError}
        error={accounts.error}
        onRetry={() => void accounts.refetch()}
        emptyMessage="No sales accounts."
      />

      <AssignModal
        account={assignFor}
        onClose={() => setAssignFor(null)}
        onAssigned={() => {
          setAssignFor(null);
          void qc.invalidateQueries({ queryKey: ['salesAccounts'] });
        }}
      />
    </div>
  );
}

function AssignModal({
  account,
  onClose,
  onAssigned,
}: {
  account: SalesAccount | null;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const [email, setEmail] = useState('');
  const [type, setType] = useState<AssignmentType>('account_owner');

  const mutation = useMutation({
    mutationFn: () => {
      const input: AssignRepInput = {
        customerOrgId: account!.customerOrgId,
        assignedUserEmail: email,
        assignmentType: type,
      };
      return assignSalesRep(input);
    },
    onSuccess: onAssigned,
  });

  return (
    <Modal
      open={Boolean(account)}
      title={`Assign Rep — ${account?.customerDisplayName ?? ''}`}
      onClose={onClose}
    >
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        {mutation.isError && <div className="form-error">{errorMessage(mutation.error)}</div>}
        <Field label="Rep email">
          <input
            className="field__input"
            type="email"
            value={email}
            required
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Assignment type">
          <select
            className="field__select"
            value={type}
            onChange={(e) => setType(e.target.value as AssignmentType)}
          >
            {ASSIGNMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </Field>
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
